/**
 * Run heavy, put-off-able work only in a quiet moment: nothing touched, scrolled or typed for `quietMs`, the app on
 * screen, and not before `afterMs` from now. iOS has no requestIdleCallback, so this is the app's own idle (2026-10-30:
 * the map pre-build and the 14 MB shape save each ran right as Sam tapped, and the tap waited seconds).
 * `maxWaitMs`: run by then even if you are still touching (work that must happen soon). Returns a cancel function.
 */
let lastInput = 0;
let lastShown = 0; // when the app last came back on screen: `afterMs` counts from the later of launch and that
let listening = false;
function listen() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  const mark = () => {
    lastInput = Date.now();
  };
  for (const ev of ['pointerdown', 'touchstart', 'scroll', 'keydown', 'wheel'])
    window.addEventListener(ev, mark, { capture: true, passive: true });
  // A resume is a launch for this purpose (2026-10-29: the Bowl prebuild ran 2 s after a return because its 12 s had
  // elapsed in the background), and the phone is busiest right after a return.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) lastShown = Date.now();
  });
}

export function whenQuiet(fn, { quietMs = 3000, afterMs = 0, maxWaitMs = Infinity } = {}) {
  listen();
  let t = 0,
    cancelled = false;
  const start = Date.now();
  const check = () => {
    if (cancelled) return;
    const quietFor = Date.now() - lastInput;
    const sinceShown = Date.now() - lastShown;
    // past maxWaitMs it runs anyway (work that must happen, just not on a tap), but never while the app is hidden
    if (
      document.hidden ||
      ((quietFor < quietMs || sinceShown < afterMs) && Date.now() - start < maxWaitMs)
    ) {
      t = setTimeout(check, Math.max(400, quietMs - quietFor, afterMs - sinceShown));
      return;
    }
    fn();
  };
  t = setTimeout(check, afterMs);
  return () => {
    cancelled = true;
    clearTimeout(t);
  };
}
